/* Copyright (c) 2026 Xperi. All Rights Reserved. */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { ErrorCodeTypes } from "../../enums/ErrorCodeTypes";
import type { IErrorAdapter } from "./IErrorAdapter";
import type { NormalizedError } from "./NormalizedError";

/**
 * Adapter for Axios HTTP client errors.
 *
 * Also normalises the non-Axios failures a service call can throw before it
 * ever reaches the wire — `AppError` subclasses raised by the service-context
 * layer (token, service-location, empty/partial response) and built-in errors
 * such as `SyntaxError` from a demarshal failure — so a single adapter covers
 * every layer of one request.
 */
export class AxiosErrorAdapter implements IErrorAdapter {
  public normalizeError(error: any): NormalizedError {
    if (!error) {
      return {
        isNetworkError: false
      };
    }

    const layerFields = {
      errorCodeType: this.readErrorCodeType(error),
      serviceErrorType: this.readServiceErrorType(error),
      serviceErrorCode: this.readServiceErrorCode(error)
    };

    // Network errors: Have error.code but NO response
    if (error.code && !error.response) {
      return {
        ...layerFields,
        code: error.code,
        isNetworkError: true
      };
    }

    // API errors: Have response with status and optional error code
    if (error.response) {
      return {
        ...layerFields,
        code: error.code,
        isNetworkError: false,
        response: {
          status: error.response.status,
          data: error.response.data
        }
      };
    }

    // Fallback: Unknown error structure
    return {
      ...layerFields,
      code: error.code,
      isNetworkError: false
    };
  }

  /**
   * Reads a layer classification the thrower attached to the error.
   * Anything that is not an {@link ErrorCodeTypes} member is ignored.
   */
  private readErrorCodeType(error: any): ErrorCodeTypes | undefined {
    const declared: unknown = error.errorCodeType;
    if (typeof declared !== "string") {
      return undefined;
    }
    const isKnown: boolean = Object.values(ErrorCodeTypes).includes(
      declared as ErrorCodeTypes
    );
    return isKnown ? (declared as ErrorCodeTypes) : undefined;
  }

  /**
   * `AppError.getType()` when present (domain errors), otherwise `Error.name`
   * — which is what identifies a `SyntaxError` from a JSON demarshal failure.
   */
  private readServiceErrorType(error: any): string | undefined {
    if (typeof error.getType === "function") {
      const type: unknown = error.getType();
      if (typeof type === "string" && type.length > 0) {
        return type;
      }
    }
    return typeof error.name === "string" && error.name.length > 0
      ? error.name
      : undefined;
  }

  /**
   * `AppError.getCode()` when present. Not read from `error.code` — that is
   * the transport code (`ECONNREFUSED`, `ERR_BAD_REQUEST`) and is normalised
   * separately.
   */
  private readServiceErrorCode(error: any): string | undefined {
    if (typeof error.getCode === "function") {
      const code: unknown = error.getCode();
      if (typeof code === "string" && code.length > 0) {
        return code;
      }
    }
    return undefined;
  }
}
